import type { IconKey } from 'flow/components/ui-components/Icon';
import {
    CHANNEL,
    ChannelValues,
} from 'flow/components/tickets/messageChannels';

// The ticket origins this client supports. Any other origin, or none while the
// ticket loads, is denied by default: no reply channel, no call, no icon.
// A new channel is added here; the exhaustive switches below flag each rule.
export const TICKET_ORIGIN = Object.freeze({
    QUEUE_CHANNEL: 'queuechannel',
    CALL: 'call',
    WHATSAPP: 'whatsapp',
    EMAIL: 'email',
});

export type KnownTicketOrigin =
    (typeof TICKET_ORIGIN)[keyof typeof TICKET_ORIGIN];

const KNOWN_TICKET_ORIGINS: ReadonlySet<string> = new Set(
    Object.values(TICKET_ORIGIN),
);

export const isKnownTicketOrigin = (
    origin?: string,
): origin is KnownTicketOrigin => !!origin && KNOWN_TICKET_ORIGINS.has(origin);

const assertNever = (origin: never): never => {
    throw new Error(`Unhandled ticket origin: ${origin}`);
};

export const getReplyChannelForOrigin = (
    origin?: string,
): ChannelValues | undefined => {
    if (!isKnownTicketOrigin(origin)) return undefined;

    switch (origin) {
        case TICKET_ORIGIN.QUEUE_CHANNEL:
        case TICKET_ORIGIN.CALL:
            return CHANNEL.SMS;
        case TICKET_ORIGIN.WHATSAPP:
            return CHANNEL.WHATSAPP;
        case TICKET_ORIGIN.EMAIL:
            return CHANNEL.EMAIL;
        default:
            return assertNever(origin);
    }
};

export const originOpensOnReply = (origin?: string): boolean => {
    if (!isKnownTicketOrigin(origin)) return false;

    switch (origin) {
        case TICKET_ORIGIN.WHATSAPP:
        case TICKET_ORIGIN.EMAIL:
            return true;
        case TICKET_ORIGIN.QUEUE_CHANNEL:
        case TICKET_ORIGIN.CALL:
            return false;
        default:
            return assertNever(origin);
    }
};

// Whether the visitor's display name is a number to call or send an SMS to.
export const originContactIsPhoneNumber = (origin?: string): boolean => {
    if (!isKnownTicketOrigin(origin)) return false;

    switch (origin) {
        case TICKET_ORIGIN.QUEUE_CHANNEL:
        case TICKET_ORIGIN.CALL:
        case TICKET_ORIGIN.WHATSAPP:
            return true;
        case TICKET_ORIGIN.EMAIL:
            return false;
        default:
            return assertNever(origin);
    }
};

export const getOriginIconKey = (
    origin?: string,
    outlined?: boolean,
): IconKey | undefined => {
    if (!isKnownTicketOrigin(origin)) return undefined;

    switch (origin) {
        case TICKET_ORIGIN.QUEUE_CHANNEL:
        case TICKET_ORIGIN.CALL:
            return outlined ? 'callOutlined' : 'call';
        case TICKET_ORIGIN.WHATSAPP:
            return 'whatsapp';
        case TICKET_ORIGIN.EMAIL:
            return outlined ? 'emailOutlined' : 'email';
        default:
            return assertNever(origin);
    }
};
